'use client'

import PropTypes from 'prop-types'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './ToastStyleOverrides.css'
import { useToastStore } from '@repo/store/useToastStore'
import { useEffect } from 'react'
import { SuccessIcon } from '@repo/ui/SuccessIcon'
import { WarningIcon } from '@repo/ui/WarningIcon'
import { ErrorIcon } from '@repo/ui/ErrorIcon'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { InfoIcon } from '@repo/ui/InfoIcon'
import MUITypography from '@mui/material/Typography'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUIAlert from '@mui/material/Alert'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'

const ToastContent = ({ toastProps }) => {
  const { text, type, actions, title, clientMatterNo, highlight, closeButton } = toastProps
  const capitalisedType = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Info'

  return (
    <>
      <MUITypography variant="h5" sx={{ mt: '2px', mb: 2 }}>
        {title || capitalisedType}
      </MUITypography>
      <MUITypography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {text}
        {highlight ? (
          <span
            style={{
              fontSize: '0.675rem',
              color: '#666',
              margin: 0,
              marginTop: '8px',
              whiteSpace: 'wrap',
              fontFamily: 'monospace'
            }}
          >
            {highlight}
          </span>
        ) : null}
      </MUITypography>

      {clientMatterNo ? (
        <ClientMatterDetailsPill clientMatterNo={clientMatterNo} sx={{ marginTop: 10, width: 'auto' }} />
      ) : null}

      <MUIStack direction="row" justifyContent="flex-end" sx={{ mt: 4, mb: -2, mr: closeButton === false ? 0 : -6 }}>
        {actions?.map((action) => (
          <MUIButton
            key={action.label}
            variant="text"
            color="secondary"
            onClick={action.onClick}
            sx={{
              textTransform: 'none',
              padding: '6px',
              minWidth: '54px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {action.label}
          </MUIButton>
        ))}
      </MUIStack>
    </>
  )
}

ToastContent.propTypes = {
  toastProps: PropTypes.object
}

export const ToastCenter = () => {
  const { toasts, isOnline } = useToastStore()

  useEffect(() => {
    const lastToast = toasts[toasts.length - 1]
    if (!lastToast) return
    if (!isOnline) return

    const toastProps = { ...lastToast }

    const icon = {
      success: <SuccessIcon />,
      warning: <WarningIcon />,
      error: <ErrorIcon />,
      info: <InfoIcon />
    }

    toast[lastToast.type](<ToastContent {...toastProps} />, { ...toastProps, icon: icon[lastToast.type] })
  }, [toasts, isOnline])

  return (
    <div data-testid="ToastCenter">
      {!isOnline && (
        <MUIAlert
          severity="warning"
          variant="filled"
          data-testid="OfflineBanner"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            borderRadius: 0,
            zIndex: 1400,
            justifyContent: 'center',
            backgroundColor: 'warning.light'
          }}
        >
          You are currently offline. Please check your internet connection.
        </MUIAlert>
      )}
      <ToastContainer
        position="bottom-right"
        autoClose={6000}
        closeOnClick
        pauseOnHover
        draggable={false}
        hideProgressBar
        stacked
        closeButton={<CloseIcon sx={{ padding: '8px', color: 'black' }} />}
        style={{
          maxWidth: window.Office ? '300px' : '320px',
          marginLeft: window.Office ? '10px' : '0px',
          marginBottom: window.Office ? '10px' : '0px'
        }}
      />
    </div>
  )
}
